'use client';

import { useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useCurrentBusiness } from '@/lib/business-context';
import { getImpersonation, IMPERSONATION_ENDED_EVENT, IMPERSONATION_STORAGE_KEY } from '@/lib/api';

type Identity = { id: string; accessToken: string; supportId: string; supportExpiry?: number };
export function useInstagramInboxContext() {
  const business = useCurrentBusiness();
  const [identity, setIdentity] = useState<Identity | null>(null);
  useEffect(() => {
    let alive = true;
    const invalidate = () => setIdentity(null);
    const storageChanged = (event: StorageEvent) => {
      // A public ordering tab saves its cart on first load. That is not an identity change.
      if (event.key && event.key !== 'sweetspot_current_business_id' && event.key !== IMPERSONATION_STORAGE_KEY
        && event.key !== 'supabase.auth.token' && !event.key.startsWith('sb-')) return;
      invalidate();
    };
    window.addEventListener('storage', storageChanged);
    window.addEventListener(IMPERSONATION_ENDED_EVENT, invalidate);
    let unsubscribe: (() => void) | undefined;
    try {
      const { data } = createClient().auth.onAuthStateChange((event, session) => {
        if (!alive) return;
        const support = getImpersonation();
        setIdentity(event === 'SIGNED_OUT' || !session?.user.id || !session.access_token ? null : {
          id: session.user.id, accessToken: session.access_token, supportId: support?.user.id ?? '', supportExpiry: support?.expiresAt,
        });
      });
      unsubscribe = () => data.subscription.unsubscribe();
    } catch { setIdentity(null); }
    return () => { alive = false; unsubscribe?.(); window.removeEventListener('storage', storageChanged); window.removeEventListener(IMPERSONATION_ENDED_EVENT, invalidate); };
  }, []);
  useEffect(() => {
    if (!identity?.supportExpiry) return;
    const timer = setTimeout(() => setIdentity(null), Math.max(0, identity.supportExpiry - Date.now()));
    return () => clearTimeout(timer);
  }, [identity?.supportExpiry]);
  const businessId = business.currentBusinessId;
  if (!identity || business.loading || !businessId || business.currentBusiness?.id !== businessId || business.currentBusiness.role !== 'owner') return null;
  return { businessId, key: JSON.stringify([identity.id, identity.supportId, businessId]), session: { accessToken: identity.accessToken } };
}
export type InstagramInboxContext = NonNullable<ReturnType<typeof useInstagramInboxContext>>;
